import React, { useCallback, useEffect, useState } from "react";
import { bool, arrayOf, string } from "prop-types";
import Button from "pages/_components/Button";
import GridTable from "pages/_components/GridTable/GridTable";
import Drawer from "pages/_components/Drawer/Drawer";
import { echeqOptions } from "util/echecks";
import CustomText from "pages/_components/CustomText/CustomText";
import FormattedAmount from "pages/_components/FormattedAmount";
import * as i18n from "util/i18n";
import classNames from "classnames";
import { isIOS } from "react-device-detect";
import { Field, Form, Formik } from "formik";
import Selector from "pages/_components/fields/formik/Selector";
import { useDispatch, useSelector } from "react-redux";
import { actions as DiscountsActions, actions as discountsActions } from "reducers/discounts";
import { Col, Row } from "react-bootstrap";
import I18n from "pages/_components/I18n";
import EmptyScreenAlert from "pages/_components/EmptyScreenAlert/EmptyScreenAlert";
import TitleColumns from "./components/TitleColumns";
import EcheqList from "./components/List";
import ProductSelector from "./components/ProductSelector";
import EcheqItem from "./components/ItemRow";
import useDiscountCreation from "./hooks/useDiscountCreation";
import useEcheqFilters from "./hooks/useEcheqFilters";
import FilterTags from "../_components/FilterTags";
import FilterForm from "./components/FilterForm";
import Image from "../../_components/Image";
import SideBar from "../_components/SideBar";

const DiscountCreation = ({ isDesktop, approvedEcheqs, lotType }) => {
    const { tags, handleDelete, handleCleanFilters, setFilters } = useEcheqFilters(echeqOptions[0]);

    const dispatch = useDispatch();

    const { hasMoreData, echeqPagination, echeqFilters } = useSelector((state) => state.discounts);
    const [selectedDiscountType, setSelectedDiscountType] = useState("");

    const {
        fetching,
        fetchingSimulation,

        allowedForDiscounts,

        allEcheqsAreSelected,
        listEcheq,
        selectedEcheqs,
        setSelectedEcheqs,
        setAllEcheqsAreSelected,
        totalAmountSelected,
        setTotalAmountSelected,
        handleCheckEcheq,
        handleCheckAllEcheqs,

        availableAccounts,
        selectedAccount,
        setSelectedAccount,
        submit,
    } = useDiscountCreation();

    const [openSidebar, setOpenSidebar] = useState(false);
    const [actionDrawer, setActionDrawer] = useState({ showDrawer: false, contentDrawer: null });

    const openSideBarFilters = () => {
        setOpenSidebar(true);
    };
    const handleCloseActionDrawer = () => {
        setActionDrawer({ showDrawer: false, contentDrawer: null, title: "" });
    };

    const fetchMoreTransactions = (echeqPagination) => {
        dispatch(
            DiscountsActions.setEcheqFilters({
                ...echeqFilters,
                pageNumber: echeqPagination.pageNumber + 1,
            }),
        );
        dispatch(DiscountsActions.loadListEcheqRequest());
    };

    const discountEcheqType = ["ownEcheqType", "thirdPartyEcheqType"];

    const formikProps = {
        initialValues: {
            discountEcheqType: lotType || "",
        },
    };

    const handleChangeType = useCallback((value) => {
        setSelectedEcheqs([]);
        setAllEcheqsAreSelected(false);
        setTotalAmountSelected(0);
        setSelectedDiscountType(value);
        dispatch(
            DiscountsActions.setEcheqFilters({
                ...echeqFilters,
                discountEcheqType: value,
            }),
        );
        dispatch(discountsActions.loadListEcheqRequest());
    }, []);

    useEffect(() => {
        if (lotType) {
            handleChangeType(lotType);
        }
    }, [lotType]);

    useEffect(() => {
        if (listEcheq && approvedEcheqs) {
            const checked = listEcheq
                .filter((echeq) => approvedEcheqs.includes(echeq.cheque_id))
                .map((echeq) => echeq.cheque_id);

            const amountChecked = listEcheq
                .filter((echeq) => checked.includes(echeq.cheque_id))
                .reduce((acum, echeq) => acum + echeq.monto, 0);

            setSelectedEcheqs(checked);
            setTotalAmountSelected(amountChecked);
        }
    }, [listEcheq]);

    return (
        <>
            <Formik {...formikProps}>
                {({ values }) => (
                    <>
                        <Form id="discountEcheqForm">
                            <Row>
                                <Col xs={12} md={6}>
                                    <Field
                                        idForm="discountForm"
                                        name="discountEcheqType"
                                        label={<I18n id="discount.echeq.type.label" />}
                                        placeholder={<I18n id="discount.echeq.type.placeholder" />}
                                        component={Selector}
                                        options={
                                            discountEcheqType &&
                                            discountEcheqType.map((item) => ({
                                                value: item,
                                                label: <I18n id={`${item}.type.label`} />,
                                            }))
                                        }
                                        value={echeqFilters?.discountEcheqType}
                                        onChange={handleChangeType}
                                    />
                                </Col>
                            </Row>
                        </Form>
                        <br />
                        {echeqFilters?.discountEcheqType && (
                            <div className="container">
                                <div className="echeq-table__available-discounts">
                                    <CustomText
                                        text={`${i18n.get("echeq.discount.available")} ${i18n
                                            .get(`${echeqFilters?.discountEcheqType}.type.label`)
                                            .toLowerCase()}`}
                                    />
                                    <FormattedAmount
                                        quantity={allowedForDiscounts}
                                        currency="ARS"
                                        className="echeq-table__available-amount"
                                    />
                                </div>
                                <FilterTags
                                    tags={tags}
                                    handleDelete={handleDelete}
                                    handleCleanFilters={handleCleanFilters}
                                    openSideBarFilters={openSideBarFilters}
                                />
                                {listEcheq?.length ? (
                                    <>
                                        <EcheqList
                                            fetchingWithPreviousData
                                            fetching={fetching}
                                            items={listEcheq || []}
                                            noDataText="global.no.results"
                                            noFiltersDataText="discount.echeq.global.no.results"
                                            renderItem={(item) => (
                                                <GridTable.Container
                                                    key={`echeq-item-${item?.cheque_id}`}
                                                    className={!isDesktop && "box box-shadow"}>
                                                    <EcheqItem
                                                        className={!isDesktop && "box box-shadow"}
                                                        key={`transaction-${item?.cheque_id}`}
                                                        receibedEcheq={item}
                                                        setDrawer={setActionDrawer}
                                                        checked={selectedEcheqs.includes(item?.cheque_id)}
                                                        handleCheckEcheq={handleCheckEcheq}
                                                        isDesktop={isDesktop}
                                                        canCheck
                                                        disableCheck={!(allowedForDiscounts > 0)}
                                                    />
                                                </GridTable.Container>
                                            )}>
                                            {(listItem) => (
                                                <GridTable className="echeq-to-discount-table tablelist">
                                                    <GridTable.Header>
                                                        <GridTable.Container>
                                                            <TitleColumns
                                                                allEcheqsAreSelected={allEcheqsAreSelected}
                                                                handleCheckAllEcheqs={handleCheckAllEcheqs}
                                                                canCheck
                                                                disableCheck={!(allowedForDiscounts > 0)}
                                                            />
                                                        </GridTable.Container>
                                                    </GridTable.Header>
                                                    <GridTable.Body>{listItem}</GridTable.Body>
                                                    {hasMoreData && (
                                                        <Button
                                                            bsStyle="link"
                                                            onClick={() => fetchMoreTransactions(echeqPagination)}
                                                            image="images/figma/icon/arrow.svg"
                                                            label="global.showMore"
                                                            className="view-more"
                                                        />
                                                    )}
                                                </GridTable>
                                            )}
                                        </EcheqList>
                                        {!!listEcheq?.length && (
                                            <>
                                                {!!selectedEcheqs.length && (
                                                    <GridTable className="summary-selected-items-table box mt-6 box-shadow">
                                                        <GridTable.Container columns={3} rows={2}>
                                                            <GridTable.Data
                                                                columnStart={1}
                                                                rowStart={1}
                                                                rowWidth={isDesktop ? 2 : 1}>
                                                                <div className="discount-bubble-wrapper">
                                                                    <Image
                                                                        src={
                                                                            isIOS
                                                                                ? "images/figma/icon/success-primary.svg"
                                                                                : "images/figma/icon/success.svg"
                                                                        }
                                                                        className={classNames("svg-icon")}
                                                                    />
                                                                    <CustomText
                                                                        text={`${selectedEcheqs.length} Echeqs seleccionados`}
                                                                    />
                                                                </div>
                                                            </GridTable.Data>
                                                            {!isDesktop && (
                                                                <GridTable.Data
                                                                    className={isIOS ? "line-dashed-wrapper" : null}
                                                                    columnStart={1}
                                                                    rowStart={2}
                                                                    columnWidth={2}>
                                                                    <div className="line-dashed" />
                                                                </GridTable.Data>
                                                            )}
                                                            <GridTable.Data
                                                                alignX="end"
                                                                columnStart={2}
                                                                rowStart={isDesktop ? 1 : 3}
                                                                columnWidth={1}>
                                                                <CustomText className="" text="Monto total" />
                                                            </GridTable.Data>
                                                            <GridTable.Data
                                                                alignX="end"
                                                                columnStart={2}
                                                                rowStart={isDesktop ? 2 : 4}
                                                                columnWidth={1}>
                                                                <FormattedAmount
                                                                    quantity={totalAmountSelected}
                                                                    currency="ARS"
                                                                />
                                                            </GridTable.Data>
                                                        </GridTable.Container>
                                                    </GridTable>
                                                )}

                                                <div className="grid-table box box-shadow mt-6">
                                                    <div className="grid-table-row sm:flex md:grid-cols-12">
                                                        <div className="grid-table-data md:col-start-2 md:col-span-10">
                                                            <ProductSelector
                                                                name="debitAccount"
                                                                idField="discount-acreditation-account"
                                                                label={i18n.get("", "Cuenta de acreditación")}
                                                                placeholder={i18n.get(
                                                                    "deposits.request.filters.debitAccount.placeHolder",
                                                                )}
                                                                data={{ options: availableAccounts || [] }}
                                                                onChange={setSelectedAccount}
                                                                value={selectedAccount}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="grid-table form-actions">
                                                    <div className="grid-table-row sm:flex md:grid-cols-12">
                                                        <div className="grid-table-data md:col-start-5 md:col-span-4">
                                                            <Button
                                                                bsStyle="primary"
                                                                label="global.simulate"
                                                                loading={fetchingSimulation}
                                                                block
                                                                onClick={submit}
                                                                disabled={!selectedEcheqs.length || !selectedAccount}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </>
                                ) : (
                                    <EmptyScreenAlert message="discount.echeq.global.no.results" />
                                )}

                                <SideBar
                                    isDesktop={isDesktop}
                                    openSidebar={openSidebar}
                                    setOpenSidebar={setOpenSidebar}>
                                    <FilterForm setOpenSidebar={setOpenSidebar} setFilters={setFilters} />
                                </SideBar>
                                <Drawer openSidebar={actionDrawer.showDrawer} onClose={handleCloseActionDrawer}>
                                    {actionDrawer.contentDrawer}
                                </Drawer>
                            </div>
                        )}
                    </>
                )}
            </Formik>
        </>
    );
};

DiscountCreation.propTypes = {
    isDesktop: bool.isRequired,
    approvedEcheqs: arrayOf(string).isRequired,
};

export default DiscountCreation;
